import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { auth as authApi } from '../services/api/auth';
import { AuthStorage } from './AuthStorage';
import { JwtUtils } from './JwtUtils';

export const AuthContext = createContext(null);

/**
 * Derive the active authentication method from the backend's auth_fallback hint
 * and the refresh token type. Exported for direct testing.
 */
export function deriveAuthMethod(authFallback, refreshToken) {
    if (authFallback === 'grace_jwt' || authFallback === 'session_bearer') return 'grace';
    // Any other backend hint means the session follows the standard (non-grace, non-local) auth path.
    if (authFallback) return 'bifrost';
    // No hint from the backend — infer from the token type.
    if (JwtUtils.isLocalRefreshToken(refreshToken)) return 'local';
    return 'bifrost';
}

function setAxiosAuthHeader(token) {
    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete axios.defaults.headers.common['Authorization'];
    }
}

/** Map an already-normalized response payload (from AuthApi) to the user object stored in context + AuthStorage. */
export function buildNextUser(data) {
    const authFallback = data.authFallback;
    return {
        token: data.token,
        refresh_token: data.refresh_token,
        username: data.username,
        user_id: data.user_id,
        staff: data.staff,
        permissions: Array.isArray(data.permissions) ? data.permissions : [],
        groups: Array.isArray(data.groups) ? data.groups : [],
        expiresAt: data.expiresAt ?? null,
        authMethod: deriveAuthMethod(authFallback, data.refresh_token),
    };
}

export function isRolePermissionError(err) {
    const detail = err?.response?.data?.detail;
    return typeof detail === 'string' && detail.includes('required') && detail.includes('role');
}

/** Restore session from localStorage synchronously so the app shell can render before validate() finishes. */
function initialAuthState() {
    try {
        const u = AuthStorage.getUser();
        if (u?.token) {
            if (!u.authMethod) {
                u.authMethod = deriveAuthMethod(u.authFallback, u.refresh_token);
            }
            setAxiosAuthHeader(u.token);
            return { user: u, loggedIn: true, checking: true };
        }
    } catch (err) {
        if (err?.status !== 401) {
            console.warn('[Mimir auth] Failed to restore session from storage:', err?.message);
        }
    }
    return { user: null, loggedIn: false, checking: false };
}

export function AuthProvider({ children }) {
    const initialSessionRef = useRef(null);
    if (initialSessionRef.current === null) {
        initialSessionRef.current = initialAuthState();
    }
    const init = initialSessionRef.current;

    const [user, setUser] = useState(init.user);
    const [loggedIn, setLoggedIn] = useState(init.loggedIn);
    const [checking, setChecking] = useState(init.checking);
    const [permissionDenied, setPermissionDeniedState] = useState(() => AuthStorage.isPermissionDenied());
    const loggingOutRef = useRef(false);
    const refreshPromiseRef = useRef(null);
    // Set when a refresh fails transiently (network/backend briefly down) and the session
    // was kept: signals the reconnect listeners below to retry once connectivity returns.
    const needsReconnectRef = useRef(false);

    const setPermissionDenied = useCallback((value) => {
        setPermissionDeniedState(value);
        AuthStorage.setPermissionDenied(value);
    }, []);

    const clearPermissionDenied = useCallback(() => setPermissionDenied(false), [setPermissionDenied]);

    const loginWithTokens = useCallback((nextUser) => {
        if (!nextUser?.token) {
            console.error('[Mimir auth] loginWithTokens called without a valid user/token — ignoring');
            return;
        }
        const user = nextUser.authMethod
            ? nextUser
            : {
                  ...nextUser,
                  authMethod: deriveAuthMethod(nextUser.authFallback, nextUser.refresh_token),
              };
        setAxiosAuthHeader(user.token);
        setUser(user);
        setLoggedIn(true);
        AuthStorage.saveUser(user);
    }, []);

    const handleAuthFailure = useCallback(
        (err) => {
            needsReconnectRef.current = false;
            if (isRolePermissionError(err)) setPermissionDenied(true);
            AuthStorage.clearUser();
            setAxiosAuthHeader(null);
            setUser(null);
            setLoggedIn(false);
        },
        [setPermissionDenied],
    );

    const validate = useCallback(async () => {
        // Do not call setChecking(true) here — initial state is already true. Setting it again
        // during bootstrap (e.g. React Strict Mode remount) or any future re-validation would
        // flash the full-screen loading spinner and block the whole app.
        try {
            const resp = await authApi.validate();
            const nextUser = buildNextUser(resp?.data ?? resp);
            loginWithTokens(nextUser);
            return nextUser;
        } catch (err) {
            handleAuthFailure(err);
            return null;
        } finally {
            setChecking(false);
        }
    }, [loginWithTokens, handleAuthFailure]);

    const refresh = useCallback(() => {
        if (refreshPromiseRef.current) return refreshPromiseRef.current;

        refreshPromiseRef.current = authApi
            .refresh()
            .then((resp) => {
                needsReconnectRef.current = false;
                const nextUser = buildNextUser(resp?.data ?? resp);
                loginWithTokens(nextUser);
                return nextUser;
            })
            .catch((err) => {
                if (authApi.isTransientFailure(err)) {
                    // Backend/network briefly unreachable (typically right after the machine
                    // wakes and the network stack is still coming up). Keep the session and
                    // retry once connectivity returns rather than forcing a full re-login.
                    needsReconnectRef.current = true;
                    return null;
                }
                handleAuthFailure(err);
                return null;
            })
            .finally(() => {
                refreshPromiseRef.current = null;
            });

        return refreshPromiseRef.current;
    }, [loginWithTokens, handleAuthFailure]);

    const logout = useCallback(async () => {
        if (loggingOutRef.current) return;
        loggingOutRef.current = true;
        try {
            await authApi.logout();
        } catch (err) {
            console.warn('[Mimir auth] logout request failed:', err?.message);
        } finally {
            AuthStorage.clearUser();
            setAxiosAuthHeader(null);
            setUser(null);
            setLoggedIn(false);
            loggingOutRef.current = false;
            // Let session-scoped caches (e.g. external workflow managers) drop their
            // data so the next user in this tab never sees the previous user's.
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('mimir:auth-cleared'));
            }
        }
    }, []);

    useEffect(() => {
        // On 403: flag permission denied so the UI can react.
        // On 401: try refresh (token may have expired) then retry the original request.
        // Auth endpoints are excluded so forms handle their own errors inline.
        const interceptorId = axios.interceptors.response.use(
            (resp) => resp,
            async (err) => {
                const status = err?.response?.status;
                const originalRequest = err?.config;
                const url = originalRequest?.url ?? '';

                if (url.includes('/api/auth/')) return Promise.reject(err);

                if (status === 403 && isRolePermissionError(err)) {
                    setPermissionDenied(true);
                    return Promise.reject(err);
                }

                if (status === 401 && !loggingOutRef.current && originalRequest && !originalRequest._retry) {
                    originalRequest._retry = true;
                    const refreshed = await refresh();
                    if (refreshed?.token) {
                        originalRequest.headers = {
                            ...originalRequest.headers,
                            Authorization: `Bearer ${refreshed.token}`,
                        };
                        return axios(originalRequest);
                    }
                    // Refresh failed — session already cleared by handleAuthFailure.
                    // Don't fall through to the redirect; let loggedIn=false handle navigation.
                    return Promise.reject(err);
                }

                return Promise.reject(err);
            },
        );

        return () => {
            axios.interceptors.response.eject(interceptorId);
        };
    }, [logout, refresh, setPermissionDenied]);

    useEffect(() => {
        // After a transient refresh failure the session is kept but stale. Retry the moment
        // connectivity is likely back: the network came online, or the tab became visible
        // again (the machine woke from sleep / the user unlocked the screen).
        const attemptReconnect = () => {
            if (needsReconnectRef.current) void refresh();
        };
        const onVisible = () => {
            if (document.visibilityState === 'visible') attemptReconnect();
        };
        window.addEventListener('online', attemptReconnect);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('online', attemptReconnect);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refresh]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setChecking((prev) => {
                if (prev) console.warn('[Mimir auth] Session validation timed out after 15s');
                return false;
            });
        }, 15000);
        void validate().finally(() => clearTimeout(timeoutId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            user,
            loggedIn,
            checking,
            permissionDenied,
            setPermissionDenied,
            clearPermissionDenied,
            validate,
            refresh,
            logout,
            loginWithTokens,
        }),
        [
            user,
            loggedIn,
            checking,
            permissionDenied,
            setPermissionDenied,
            clearPermissionDenied,
            validate,
            refresh,
            logout,
            loginWithTokens,
        ],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}
